import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AdminCertificados.css';

const AdminCertificados = () => {
  const { user } = useAuth();
  const [certificados, setCertificados] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('certificados'); // 'certificados' ou 'solicitacoes'
  const [prolongando, setProlongando] = useState(null);
  const [mesesAdicionais, setMesesAdicionais] = useState(12);

  useEffect(() => {
    if (user?.tipo_usuario === 'admin') {
      carregarDados();
    }
  }, [user, abaAtiva]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (abaAtiva === 'certificados') {
        const response = await api.get('/certificados/admin/listar?limit=100');
        if (response.data.success) {
          setCertificados(response.data.data);
        }
      } else {
        const response = await api.get('/certificados/admin/solicitacoes?status=todas');
        if (response.data.success) {
          setSolicitacoes(response.data.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovarSolicitacao = async (idSolicitacao) => {
    try {
      setError('');
      const response = await api.post('/certificados/admin/aprovar-solicitacao', {
        id_solicitacao: idSolicitacao
      });
      
      if (response.data.success) {
        setSuccess('Certificado emitido com sucesso!');
        await carregarDados();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao aprovar solicitação');
    }
  };

  const handleRejeitarSolicitacao = async (idSolicitacao) => {
    const motivo = window.prompt('Motivo da rejeição (opcional):');
    try {
      setError('');
      const response = await api.post('/certificados/admin/rejeitar-solicitacao', {
        id_solicitacao: idSolicitacao,
        motivo_rejeicao: motivo || null
      });
      
      if (response.data.success) {
        setSuccess('Solicitação rejeitada');
        await carregarDados();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao rejeitar solicitação');
    }
  };

  const handleProlongar = async (idCertificado) => {
    try {
      setProlongando(idCertificado);
      setError('');
      const response = await api.post('/certificados/admin/prolongar', {
        id_certificado: idCertificado,
        meses_adicionais: mesesAdicionais
      });
      
      if (response.data.success) {
        setSuccess(`Validade prolongada em ${mesesAdicionais} meses`);
        await carregarDados();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao prolongar validade');
    } finally {
      setProlongando(null);
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleString('pt-BR');
  };

  const calcularDiasRestantes = (dataExpiracao) => {
    const agora = new Date();
    const expiracao = new Date(dataExpiracao);
    const diff = Math.ceil((expiracao - agora) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (user?.tipo_usuario !== 'admin') {
    return (
      <div className="admin-certificados-container">
        <div className="alert alert-error">
          Acesso restrito. Apenas administradores podem acessar esta página.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-certificados-container">
      <div className="admin-certificados-header">
        <h1>Gerenciamento de Certificados Digitais</h1>
        <p>Gerencie certificados e solicitações do sistema</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      <div className="abas">
        <button
          className={`aba ${abaAtiva === 'certificados' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('certificados')}
        >
          📋 Certificados ({certificados.length})
        </button>
        <button
          className={`aba ${abaAtiva === 'solicitacoes' ? 'ativa' : ''}`}
          onClick={() => setAbaAtiva('solicitacoes')}
        >
          📨 Solicitações ({solicitacoes.filter(s => s.status === 'pendente').length} pendentes)
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <p>Carregando...</p>
        </div>
      ) : abaAtiva === 'certificados' ? (
        <div className="certificados-lista">
          <table className="tabela-certificados">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Número de Série</th>
                <th>Status</th>
                <th>Data Emissão</th>
                <th>Data Expiração</th>
                <th>Dias Restantes</th>
                <th>Último Uso</th>
                <th>Total Usos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {certificados.map((cert) => {
                const diasRestantes = calcularDiasRestantes(cert.data_expiracao);
                return (
                  <tr key={cert.id_certificado}>
                    <td>
                      <div>
                        <strong>{cert.usuario?.nome_completo}</strong>
                        <br />
                        <small>{cert.usuario?.email}</small>
                      </div>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem' }}>{cert.numero_serie}</code>
                    </td>
                    <td>
                      <span className={`status-badge ${cert.status}`}>
                        {cert.status === 'ativo' && diasRestantes > 0 ? '✅ Ativo' : ''}
                        {cert.status === 'expirado' ? '⏰ Expirado' : ''}
                        {cert.status === 'revogado' ? '❌ Revogado' : ''}
                        {cert.status === 'renovado' ? '🔄 Renovado' : ''}
                      </span>
                    </td>
                    <td>{formatarData(cert.data_emissao)}</td>
                    <td>{formatarData(cert.data_expiracao)}</td>
                    <td>
                      <span className={diasRestantes > 30 ? 'text-success' : diasRestantes > 0 ? 'text-warning' : 'text-danger'}>
                        {diasRestantes > 0 ? `${diasRestantes} dias` : 'Expirado'}
                      </span>
                    </td>
                    <td>
                      {cert.ultimo_uso ? (
                        <div>
                          <small>{formatarData(cert.ultimo_uso.data)}</small>
                          <br />
                          <small style={{ color: '#6B7280' }}>{cert.ultimo_uso.tipo_uso}</small>
                        </div>
                      ) : (
                        <span style={{ color: '#9CA3AF' }}>Nunca usado</span>
                      )}
                    </td>
                    <td>{cert.total_usos || 0}</td>
                    <td>
                      {cert.status === 'ativo' && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={mesesAdicionais}
                            onChange={(e) => setMesesAdicionais(parseInt(e.target.value))}
                            style={{ width: '60px', padding: '0.25rem' }}
                            disabled={prolongando === cert.id_certificado}
                          />
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleProlongar(cert.id_certificado)}
                            disabled={prolongando === cert.id_certificado}
                          >
                            {prolongando === cert.id_certificado ? 'Prolongando...' : 'Prolongar'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="solicitacoes-lista">
          {solicitacoes.filter(s => s.status === 'pendente').length === 0 ? (
            <div className="empty-state">
              <p>Nenhuma solicitação pendente</p>
            </div>
          ) : (
            solicitacoes
              .filter(s => s.status === 'pendente')
              .map((solic) => (
                <div key={solic.id_solicitacao} className="solicitacao-card">
                  <div className="solicitacao-header">
                    <div>
                      <h3>{solic.solicitante?.nome_completo}</h3>
                      <p>{solic.solicitante?.email}</p>
                    </div>
                    <span className="status-badge pendente">Pendente</span>
                  </div>
                  <div className="solicitacao-info">
                    <div className="info-item">
                      <strong>Validade solicitada:</strong> {solic.validade_solicitada} {solic.validade_solicitada === 1 ? 'ano' : 'anos'}
                    </div>
                    <div className="info-item">
                      <strong>Algoritmo:</strong> {solic.algoritmo_solicitado}
                    </div>
                    <div className="info-item">
                      <strong>Tamanho da chave:</strong> {solic.tamanho_chave_solicitado} bits
                    </div>
                    {solic.motivo && (
                      <div className="info-item">
                        <strong>Motivo:</strong> {solic.motivo}
                      </div>
                    )}
                    <div className="info-item">
                      <strong>Solicitado em:</strong> {formatarData(solic.criado_em)}
                    </div>
                  </div>
                  <div className="solicitacao-actions">
                    <button
                      className="btn btn-success"
                      onClick={() => handleAprovarSolicitacao(solic.id_solicitacao)}
                    >
                      ✅ Aprovar e Emitir
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleRejeitarSolicitacao(solic.id_solicitacao)}
                    >
                      ❌ Rejeitar
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCertificados;

